import { RECURRENCE_CADENCE } from 'lib';
import React, { useMemo, useState } from 'react';
import { ModalV2 } from 'components';
import { Btn, Field, Grid, GroupLabel, Inset, Note, Rule, Select, T, TextInput } from './ui';
import { listFieldLabels } from './campaignRequirements';
import { wallClockToUtc } from './timezone';

/**
 * Section 5 - giving a campaign a time.
 *
 * The timezone select is not decoration. A campaign sends at one instant, and an operator enters
 * that instant as a local time - so without saying which zone they mean, "09:30" is ambiguous the
 * moment anyone schedules for an audience outside their own. The zone is stored with the instant
 * and shown wherever a send time is displayed; a bare time would be unreadable.
 *
 * The zone list is short on purpose: the audience in the design spans these regions, and a
 * six-hundred-entry IANA dropdown is worse than a curated one. `Intl` supplies the browser's own
 * zone as the default, which is right far more often than any fixed choice.
 */
const ZONES = [
  'Europe/London',
  'Europe/Paris',
  'Europe/Stockholm',
  'Europe/Tallinn',
  'America/New_York',
  'America/Los_Angeles',
  'America/Argentina/Buenos_Aires',
  'Asia/Tokyo',
  'UTC'
];

/** Not a cadence — the absence of one, and the default. */
const ONCE = 'once';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * The wall-clock time now, in one zone, as an `<input type="time">` value.
 *
 * `hourCycle: 'h23'` for the 24-hour `HH:mm` the input requires. Not `hour12: false`, which asks
 * for the same thing less precisely: several engines answer it with the h24 cycle, where midnight
 * is `24:00` — a string `<input type="time">` rejects, so the field would come up empty for anyone
 * opening the scheduler in the first minute of their day. The `24:` guard below is the second line
 * of that defence, for an engine that ignores the cycle.
 *
 * `Intl` rather than a date library: it is already how this file and
 * `api/email/lib/scheduleTimezone.ts` do zone arithmetic, so this adds no dependency and no second
 * idea of what a zone means.
 *
 * Falls back to the browser's own clock if the zone is one `Intl` refuses, which is the same
 * posture `browserZone` takes: a time that may be an hour out is still a usable starting point,
 * where a crash in a scheduling panel is not.
 */
const clockTimeIn = (zone) => {
  try {
    const formatted = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).format(new Date());

    return formatted.startsWith('24:') ? `00:${formatted.slice(3)}` : formatted;
  } catch {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  }
};

const ScheduleCalendar = ({
  onSchedule,
  onSendNow,
  onTestSend,
  onSetRecurrence,
  onCancel,
  testSendResult,
  /** The stop-window a queued send left behind: `{ until, minutes }`. */
  sendWindow,
  /** How many recipients the current filters match. Stated on the confirmation. */
  matching,
  /** False when no audience has been chosen, or when it matches nobody. */
  audienceReady,
  audienceReason,
  /**
   * Whether this audience must not be *scheduled*, as well as not sent now.
   *
   * Separate from `audienceReady`, which has only ever gated Send Now. Scheduling was left open
   * deliberately — an operator can schedule a campaign on Monday for Friday and fix its audience
   * on Wednesday, and the audience is re-resolved at send time anyway.
   *
   * That reasoning does not survive a channel audience. Its count is final rather than an
   * estimate, and the server refuses to schedule one that is empty or whose channel has gone
   * (FR-010, FR-011, FR-018) — so leaving the button open here would mean an operator pressing it
   * and reading the refusal as a fault rather than as the screen already knew.
   *
   * Set by the page only for a channel campaign, so the saved-segment path schedules exactly as it
   * always has.
   */
  audienceBlocksSchedule,
  /**
   * The builder's required fields that are still empty (`missingCampaignFields`).
   *
   * Listed above the buttons, all at once, each a link back to its field — and they block
   * scheduling as well as sending. A scheduled campaign can no longer be edited, so one scheduled
   * with no challenge or no audience would go out exactly that way.
   */
  missingFields = [],
  onShowMissingField,
  busy,
  isRunning,
  canSchedule,
  reason,
  /** The stored policy. Displayed here, editable only on Settings. */
  policy,
  onEditPolicy,
  /**
   * What fills the column beside these controls — the campaign list, supplied by the page.
   *
   * A node rather than `campaigns`, because the list has three load states and four actions that
   * belong to the shell, and threading all of them through a scheduler would make this component
   * about campaigns. It renders a column; the page decides what is in it.
   */
  aside
}) => {
  // The tooltip's fallback: an audience reason is more specific, so it is shown first when there is one.
  const missingMessage = missingFields.length
    ? `Complete the required fields first: ${listFieldLabels(missingFields)}.`
    : '';
  const [confirming, setConfirming] = useState(false);

  /*
    The cap as stored, with the endpoint's own defaults when the policy has not loaded yet — the
    same pair `getFatiguePolicy` returns, so an unloaded panel states the fallback the server would
    apply rather than a number invented here.
  */
  const cap = { maxPerWindow: 1, windowHours: 24, ...(policy?.fatigue || {}) };
  const capped = policy?.fatigue?.enabled ?? true;
  const browserZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  }, []);

  const [date, setDate] = useState('');
  /*
    Seeded with the time it is now, not with 09:30.

    The fixed default was a time of day somebody might plausibly want, which is a different thing
    from a time the operator can measure against. Scheduling is nearly always expressed relative to
    now — "in twenty minutes", "after the match" — and a field reading 09:30 at four in the
    afternoon gives them nothing to count from, so they read the clock off their phone instead.

    Seeded once, at mount, in the browser's zone: this is a starting value the operator edits, and
    a field that rewrote itself while being typed into would be worse than a slightly stale one.
    The hint beside it is what stays current, including when the zone is changed.
  */
  const [time, setTime] = useState(() => clockTimeIn(browserZone));
  const [zone, setZone] = useState(browserZone);
  const [cadence, setCadence] = useState(ONCE);
  // Thursday, because the design's own example is "Weekly every Thursday".
  const [dayOfWeek, setDayOfWeek] = useState(4);
  const [dayOfMonth, setDayOfMonth] = useState(1);

  const zoneOptions = useMemo(() => {
    const all = ZONES.includes(browserZone) ? ZONES : [browserZone, ...ZONES];
    return all.map((value) => ({ value, label: value.replace(/_/g, ' ') }));
  }, [browserZone]);

  /*
    A repeating campaign is set up, not scheduled, and Send Now has nothing to act on — the parent
    never sends. So the primary button changes verb and the two one-off actions step aside rather
    than sending something the operator did not mean.
  */
  const repeating = cadence !== ONCE;

  const scheduledAt = date && time ? `${date}T${time}` : '';

  /*
    Evaluated in the zone the operator chose, not the browser's.

    `new Date('2026-09-12T09:00')` parses in the *browser's* zone while the operator has separately
    picked `zone` above — so scheduling 09:00 Asia/Tokyo from London either blocked a valid future
    time or admitted one already past, and the client and server disagreed about which. Mirrors
    `api/email/lib/scheduleTimezone.ts`, which solves the same problem with the platform's own
    `Intl` and says why it adds no timezone library for twenty lines of arithmetic.
  */
  const past = scheduledAt ? wallClockToUtc(scheduledAt, zone) <= Date.now() : false;

  return (
    /*
      Controls on the left, the campaigns on the right, as the design arranges this frame.

      What is on the right is not decoration: it is the answer to "did the thing I just scheduled
      land", asked at the bottom of the panel that scheduled it. The list lived on the dashboard,
      one navigation away, which is how an operator ends up pressing Schedule twice.

      40% to the controls and 60% to the list, at the owner's request: the controls are a short
      column of fields, and the list is a five-column table that was cramped at half the width.
      (Briefly 35/65; the owner settled on 40/60.)

      `Grid` collapses to one column below 1100px, so on a narrow screen the controls come first and
      the list follows — the order the work is done in.
    */
    <Grid cols="minmax(0, 40fr) minmax(0, 60fr)" gap="26px">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        {/*
          Labelled for what they set, not for the widget that sets it.

          "Date Picker", "Time Picker" and "Time Zone Selector" are the Figma layer names, carried
          straight into the interface — they describe the control rather than the thing being
          chosen, which is what a label is for. Three fields on one panel all named "…Picker" also
          gave an operator nothing to distinguish them by except the widget shape.
        */}
        <Grid cols="1fr 1fr" gap="12px">
          <Field label="Send date">
            <TextInput type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </Field>
          {/*
            The hint reads the chosen zone, not the browser's.

            An operator scheduling Tokyo time from London needs "it is 23:40 there now" rather than
            their own clock — the whole point of the reference is that the time in the field above
            is read in `zone`. Recomputed on render rather than ticking on a timer: this panel
            re-renders on every keystroke in it, and a second interval running behind a scheduling
            screen is a cost with no reader.
          */}
          <Field
            label="Send time"
            hint={`It is ${clockTimeIn(zone)} in ${zone.replace(/_/g, ' ')} now.`}
          >
            <TextInput type="time" value={time} onChange={(event) => setTime(event.target.value)} />
          </Field>
        </Grid>

        <Field
          label="Time zone for that time"
          hint="The date and time above are read in this zone. The instant itself is stored in UTC, so it does not move when the clocks change."
        >
          <Select value={zone} onChange={(event) => setZone(event.target.value)}>
            {zoneOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Recurrence"
          hint={
            cadence === ONCE
              ? 'A single send at the time above.'
              : 'Each occurrence is listed and reported on separately, and sends to the audience as it stands on the day.'
          }
        >
          <Select value={cadence} onChange={(event) => setCadence(event.target.value)}>
            <option value={ONCE}>Once, at the time above</option>
            <option value={RECURRENCE_CADENCE.DAILY}>Daily</option>
            <option value={RECURRENCE_CADENCE.WEEKLY}>Weekly</option>
            <option value={RECURRENCE_CADENCE.MONTHLY}>Monthly</option>
          </Select>
        </Field>

        {cadence === RECURRENCE_CADENCE.WEEKLY ? (
          <Field label="Repeats On">
            <Select
              value={String(dayOfWeek)}
              onChange={(event) => setDayOfWeek(Number(event.target.value))}
            >
              {WEEKDAYS.map((label, index) => (
                <option key={label} value={String(index)}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        {cadence === RECURRENCE_CADENCE.MONTHLY ? (
          <Field
            label="Day of the Month"
            hint="29, 30 and 31 fall back to the month's last day rather than skipping the month."
          >
            <TextInput
              type="number"
              min="1"
              max="31"
              value={dayOfMonth}
              onChange={(event) => setDayOfMonth(Number(event.target.value))}
            />
          </Field>
        ) : null}

        {/*
          What will be enforced on the send about to be scheduled, stated beside the button that
          schedules it.

          This panel was deliberately absent once, and the objection was a good one: a second place
          showing the fatigue numbers is a second place to forget when they change, and the copy it
          had then was only right by coincidence. So it holds no numbers of its own — every value
          here is read from the stored policy, and Settings remains the only place to change any of
          it.

          The lines are what this platform actually does, not what the design's mock-up lists. The
          dispatcher suppresses unsubscribed fans, skips unverified addresses and applies the cap;
          a repeated trigger is stopped by a unique index. Hard-bounce suppression is built and
          inactive until a delivery-reporting provider is switched on, so it renders as not in
          force — a tick beside something that is not happening is a false compliance claim.
        */}
        <Inset tint role="group" aria-label="Active guardrail / automation rules">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 9
            }}
          >
            <GroupLabel style={{ color: T.primary }}>
              Active guardrail / automation rules
            </GroupLabel>
            {onEditPolicy ? (
              <Btn variant="outline" onClick={onEditPolicy}>
                Change in Settings
              </Btn>
            ) : null}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <Rule on={capped}>
              {capped
                ? `Max ${cap.maxPerWindow} engagement email${
                    cap.maxPerWindow === 1 ? '' : 's'
                  } per fan per ${cap.windowHours} hour${cap.windowHours === 1 ? '' : 's'}`
                : 'Cap switched off — every campaign reaches every match'}
            </Rule>
            <Rule>Fans who unsubscribed are suppressed before sending</Rule>
            <Rule>Addresses that were never verified are skipped</Rule>
            <Rule>A repeated trigger cannot send twice — enforced by a unique index</Rule>
            <Rule on={false}>
              Hard-bounced addresses are suppressed automatically — built, and active once a
              delivery-reporting provider is switched on
            </Rule>
          </div>
        </Inset>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {missingFields.length ? (
            <Inset>
              <GroupLabel style={{ color: T.red }}>
                {`Complete ${missingFields.length === 1 ? 'this required field' : `these ${missingFields.length} required fields`} before scheduling or sending`}
              </GroupLabel>
              <ul
                role="status"
                aria-label="Required fields still to complete"
                style={{
                  margin: '8px 0 0',
                  paddingLeft: 18,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                {missingFields.map((field) => (
                  <li key={field.key} style={{ fontSize: 13, color: T.body, lineHeight: 1.5 }}>
                    <button
                      type="button"
                      onClick={() => onShowMissingField?.(field.key)}
                      style={{
                        padding: 0,
                        border: 0,
                        background: 'none',
                        color: T.primary,
                        font: 'inherit',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                    >
                      {field.label}
                    </button>
                    {` — ${field.message}`}
                  </li>
                ))}
              </ul>
            </Inset>
          ) : null}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Btn
              icon="Calendar"
              onClick={() =>
                repeating
                  ? onSetRecurrence({
                      cadence,
                      dayOfWeek: cadence === RECURRENCE_CADENCE.WEEKLY ? dayOfWeek : undefined,
                      dayOfMonth: cadence === RECURRENCE_CADENCE.MONTHLY ? dayOfMonth : undefined,
                      timeOfDay: time,
                      timezone: zone,
                      startAt: scheduledAt
                    })
                  : onSchedule({ scheduledAt, timezone: zone })
              }
              disabled={
                busy ||
                !canSchedule ||
                !scheduledAt ||
                past ||
                audienceBlocksSchedule ||
                missingFields.length > 0
              }
              title={
                audienceBlocksSchedule
                  ? audienceReason
                  : missingFields.length
                    ? missingMessage
                    : canSchedule
                      ? undefined
                      : reason
              }
            >
              {/*
                Reads its own action, not the shared flag.

                Every one of these three buttons was disabled and relabelled by any action anywhere
                in the screen, so pressing Test Send made this one read "Scheduling..." — reporting
                an operation nobody had started, on the control that would have started it.
              */}
              {isRunning(repeating ? 'set-recurrence' : 'schedule')
                ? repeating
                  ? 'Setting up…'
                  : 'Scheduling…'
                : repeating
                  ? 'Set Up Repeating Campaign'
                  : 'Schedule Campaign'}
            </Btn>
            <Btn
              variant="outline"
              icon="Send"
              onClick={() => setConfirming(true)}
              disabled={busy || !canSchedule || repeating || !audienceReady}
              title={
                !audienceReady
                  ? audienceReason || missingMessage
                  : canSchedule
                    ? 'Queues the campaign five minutes from now. You can still cancel it during those five minutes.'
                    : reason
              }
            >
              {isRunning('send-now') ? 'Queueing…' : 'Send Now'}
            </Btn>
            <Btn
              variant="outline"
              icon="Mail"
              onClick={onTestSend}
              disabled={busy || !canSchedule || repeating}
              title={
                canSchedule
                  ? 'Sends one copy to your own address. It does not count towards the campaign results.'
                  : reason
              }
            >
              {isRunning('test-send') ? 'Sending proof…' : 'Test Send to Admin'}
            </Btn>
          </div>

          {/*
            The stop deadline, and the control that uses it.

            `sendCampaignNow` has always returned `cancellableUntil`, the client threw it away, and
            the cancel control lived on another page — so an operator was told a five-minute window
            existed and never told when it closed or how to use it.
          */}
          {sendWindow ? (
            <Inset tint>
              <GroupLabel style={{ color: T.primary }}>Queued — still stoppable</GroupLabel>
              <Note>
                {`This sends at ${new Date(sendWindow.until).toLocaleTimeString()}. You have about ${sendWindow.minutes} minutes to stop it.`}
              </Note>
              <div style={{ marginTop: 10 }}>
                <Btn
                  variant="danger"
                  onClick={() => onCancel(sendWindow.campaignId)}
                  disabled={busy}
                >
                  Stop this send
                </Btn>
              </div>
            </Inset>
          ) : null}

          {/*
            One click used to be all it took, with no recipient count anywhere near the button —
            the audience size was a sentence in a different card. This is the only irreversible
            action on the screen.
          */}
          <ModalV2
            visible={confirming}
            closeModal={() => setConfirming(false)}
            width="440px"
            ariaLabel="Confirm sending this campaign"
            header="Send this campaign?"
            footer={
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <Btn variant="outline" onClick={() => setConfirming(false)} disabled={busy}>
                  Keep editing
                </Btn>
                <Btn
                  onClick={() => {
                    setConfirming(false);
                    onSendNow();
                  }}
                  disabled={busy}
                >
                  {`Send to ${(matching ?? 0).toLocaleString()} ${matching === 1 ? 'fan' : 'fans'}`}
                </Btn>
              </div>
            }
          >
            <Note>
              {`This queues the campaign for ${(matching ?? 0).toLocaleString()} ${
                matching === 1 ? 'recipient' : 'recipients'
              }, five minutes from now.`}
            </Note>
            <Note>
              You can stop it during those five minutes, and not afterwards — once sending starts,
              some recipients already have it.
            </Note>
          </ModalV2>

          {past ? (
            <Note style={{ color: T.red }}>
              That time has already passed. The scheduler only looks forward, so this would never
              run.
            </Note>
          ) : null}
          {!canSchedule ? <Note>{reason}</Note> : null}
          {/*
              Announced: it carries the rate-limit reset time, which is the one thing that makes a
              refusal actionable, and it appeared silently.
            */}
          {testSendResult ? (
            <Note role="status" aria-live="polite">
              {testSendResult}
            </Note>
          ) : null}
          <Note>
            Send Now queues the campaign five minutes out rather than dispatching instantly, so
            there is still a window to cancel it. A campaign cannot be un-sent once it starts.
          </Note>
        </div>
      </div>

      {aside ? <div style={{ minWidth: 0 }}>{aside}</div> : null}
    </Grid>
  );
};

export default ScheduleCalendar;
